import { driverAppEntry, endSession } from "@khazzin/auth";
import { Icon } from "@khazzin/ui/icons/Icon";
import {
	ImpersonationBanner,
	INBOX_QUERY_KEY,
	InboxPage,
	NotificationBell,
	TOPIC_NOTIFICATION_CREATED,
	useInboxSource,
} from "@khazzin/ui/patterns";
import {
	type TopicKeys,
	useEventStream,
	usePresenceHeartbeat,
	useStreamConnection,
} from "@khazzin/ui/patterns/live";
import { EmptyState, OfflinePill } from "@khazzin/ui/patterns/page";
import { useServiceWorkerUpdate } from "@khazzin/ui/patterns/serviceWorker";
import { Button, Select } from "@khazzin/ui/primitives";
import { ToastProvider } from "@khazzin/ui/primitives/Toast";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import {
	Bell,
	Boxes,
	ChartColumn,
	CirclePlus,
	Ellipsis,
	Globe,
	LayoutDashboard,
	LifeBuoy,
	type LucideIcon,
	Plug,
	Settings,
	ShoppingCart,
	Tags,
	Truck,
	Undo2,
	Users,
	Wallet,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useApi } from "../../api";
import { pendingInvite } from "../../modules/team/team";
import { forgetImpersonation, impersonationToken } from "./impersonation";
import { useLocale } from "./locale";
import { useStoreScope } from "./scope";
import {
	bottomBar,
	type Level,
	levelOf,
	reaches,
	type SectionKey,
	sectionOff,
	sectionPath,
	visibleSections,
} from "./sections";
import { useServiceOff } from "./serviceSwitches";

/**
 * The Store Portal's shell (EP10-S02, DA-10-01).
 *
 * A sidebar on a wide screen — the store switcher at its top, then one link per
 * section the grid shows — and on a phone a bottom bar of Orders, Inventory,
 * New Order (at `write`) and More, every target at least 48px. A standing
 * impersonation banner when support is acting as the store, and an offline
 * banner when the network is gone. The live stream (EP10-S22) re-reads the
 * store's screens as its orders move, behind the connection pill.
 *
 * **A section the grid hides is not found.** Typing its address answers the
 * same "not found" the server would, never a "you may not" that confirms it
 * exists.
 */
export function Shell() {
	const { t } = useLocale();
	// The portal's toasts: a save that worked or failed says so where the
	// person is (Settings → Services and Packaging).
	return (
		<ToastProvider label={t("ui.toast.region")} closeLabel={t("ui.toast.dismiss")}>
			<ShellBody />
		</ToastProvider>
	);
}

function ShellBody() {
	const { t } = useLocale();
	const { user, isLoading, signIn } = useAuth();
	const { context, loading, failed, retry, sessionOver, store } = useStoreScope();
	const serviceOff = useServiceOff(store?.storeId ?? "");
	// EP01-S16: a Login as Store tab is signed in by its token, not by AuthKit.
	const impersonating = impersonationToken() !== null;
	const signedIn = impersonating || user !== null;

	if ((!impersonating && isLoading) || (signedIn && loading)) {
		return (
			<main className="grid min-h-screen place-items-center text-fg-muted">
				{t("portal.shell.loading")}
			</main>
		);
	}
	if (!signedIn) {
		return (
			<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
				<EmptyState
					title={t("portal.shell.title")}
					body={t("portal.shell.signedOut")}
					action={
						<Button variant="primary" className="min-h-12" onClick={() => void signIn()}>
							{t("portal.shell.signIn")}
						</Button>
					}
				/>
			</main>
		);
	}
	// ADJ-M03-4: a 401 anywhere is the session being over — one screen that
	// says so, instead of every section showing its own failure.
	if (sessionOver) {
		return (
			<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
				<EmptyState
					title={t("portal.shell.sessionOverTitle")}
					body={t("portal.shell.sessionOverBody")}
					action={<LeaveButton variant="primary" label={t("portal.shell.signInAgain")} />}
				/>
			</main>
		);
	}
	// EP10-S10: back from signing in with an invitation link still to redeem.
	if (pendingInvite()) {
		return <Navigate to="/invitations/accept" replace />;
	}
	// ADJ-M03-4: a failed `/me/context` is not "no store yet"; it can be
	// retried, and the person can always leave.
	if (failed) {
		return (
			<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
				<EmptyState
					title={t("portal.shell.loadFailedTitle")}
					body={t("portal.shell.loadFailedBody")}
					action={
						<div className="flex flex-wrap justify-center gap-2">
							<Button variant="primary" className="min-h-12" onClick={retry}>
								{t("portal.shell.retry")}
							</Button>
							<LeaveButton variant="secondary" label={t("portal.shell.signOut")} />
						</div>
					}
				/>
			</main>
		);
	}
	// Owner list 2026-10-15: a driver who signed in here belongs in the Driver
	// App, and is sent on.
	const driverApp = import.meta.env.VITE_DRIVER_APP_URL;
	if (
		store === null &&
		(context?.deliveryCompanies ?? []).length > 0 &&
		typeof driverApp === "string" &&
		driverApp !== ""
	) {
		return <ToDriverApp href={driverAppEntry(driverApp)} />;
	}
	if (store === null) {
		return (
			<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
				<EmptyState
					title={t("portal.shell.noStoreTitle")}
					body={
						user?.email === undefined
							? t("portal.shell.noStoreBody")
							: `${t("portal.shell.signedInAs", { email: user.email })} ${t("portal.shell.noStoreBody")}`
					}
					action={<LeaveButton variant="secondary" label={t("portal.shell.signOut")} />}
				/>
			</main>
		);
	}

	return (
		<div className="flex min-h-screen flex-col bg-bg-canvas">
			{context?.impersonation !== undefined && context.impersonation !== null && (
				<Impersonation
					id={context.impersonation.id}
					target={
						context.me.displayName ??
						context.impersonation.targetName ??
						store.storeName ??
						store.storeId
					}
					endsAt={context.impersonation.endsAt}
					operator={t("portal.shell.support")}
				/>
			)}
			<UpdateBar />
			<OfflineBanner />
			<div className="flex min-h-0 flex-1">
				<aside className="hidden w-60 shrink-0 flex-col gap-3 border-e border-border bg-bg-surface p-3 md:flex">
					<StoreSwitcher />
					<nav aria-label={t("portal.shell.menu")} className="flex flex-col gap-0.5">
						{visibleSections(store, serviceOff).map((key) => (
							<SectionLink key={key} section={key} />
						))}
						<AccountLinks />
					</nav>
				</aside>
				<div className="flex min-w-0 flex-1 flex-col">
					<Topbar />
					<main className="flex-1 p-4 pb-24 md:pb-4 [&>*]:animate-[km-fade-in_0.2s_var(--ease-out)] motion-reduce:[&>*]:animate-none">
						<Outlet />
					</main>
				</div>
			</div>
			<BottomBar />
			<Live storeId={store.storeId} />
		</div>
	);
}

/**
 * A newer portal build is deployed (EP10-S23): checked on every navigation,
 * shown as a bar that blocks nothing. Reload moves onto it.
 */
function UpdateBar() {
	const { t } = useLocale();
	const { pathname } = useLocation();
	const { available, apply } = useServiceWorkerUpdate(pathname);
	if (!available) {
		return null;
	}
	return (
		<div
			role="status"
			className="flex flex-wrap items-center justify-center gap-3 border-b border-info-border bg-info-bg px-4 py-2 text-sm text-info-fg"
		>
			<span>{t("common.upgrade.available")}</span>
			<button
				type="button"
				onClick={apply}
				className="min-h-10 rounded-sm border border-info-border px-3 py-1 font-bold text-info-fg hover:bg-bg-surface"
			>
				{t("common.upgrade.reload")}
			</button>
		</div>
	);
}

/** The store's cached reads each topic makes stale (EP10-S22). */
export function storeTopics(storeId: string): TopicKeys {
	const orders = ["portal", storeId, "orders"];
	// EP10-S13: the dashboard's tiles and feed move with orders and stock.
	const dashboard = ["portal", storeId, "dashboard"];
	return {
		"order.created": [orders, dashboard],
		"order.status_changed": [orders, dashboard],
		"shipment.status_changed": [orders],
		"shipment.assigned": [orders],
		"inventory.stock_changed": [["portal", storeId, "inventory"], dashboard],
		// EP12-S03: the bell and the inbox, which are the person's, not the store's.
		[TOPIC_NOTIFICATION_CREATED]: [INBOX_QUERY_KEY],
	};
}

/** The store's stream, one per store (a switch reconnects), its presence and the pill. */
function Live({ storeId }: { storeId: string }) {
	const { t } = useLocale();
	const api = useApi();
	const topics = useMemo(() => storeTopics(storeId), [storeId]);
	usePresenceHeartbeat(api, () => ({ interface: "store" }));
	const status = useStreamConnection(useEventStream(api, topics, { scope: storeId }));
	return <OfflinePill status={status} t={t} />;
}

/** Each section's glyph, in the sidebar and the phone's tab bar alike. */
const SECTION_ICONS: Record<SectionKey | "more", LucideIcon> = {
	dashboard: LayoutDashboard,
	orders: ShoppingCart,
	external_import: Globe,
	new_order: CirclePlus,
	inventory: Boxes,
	catalog: Tags,
	returns: Undo2,
	finance: Wallet,
	delivery_rates: Truck,
	analytics: ChartColumn,
	integrations: Plug,
	team: Users,
	settings: Settings,
	more: Ellipsis,
};

function SectionLink({ section, className = "" }: { section: SectionKey; className?: string }) {
	const { t } = useLocale();
	return (
		<MenuLink to={sectionPath(section)} icon={SECTION_ICONS[section]} className={className}>
			{t(`portal.shell.sections.${section}`)}
		</MenuLink>
	);
}

function MenuLink({
	to,
	icon,
	className = "",
	children,
}: {
	to: string;
	icon?: LucideIcon;
	className?: string;
	children: ReactNode;
}) {
	return (
		<NavLink
			to={to}
			className={({ isActive }) =>
				`relative flex min-h-10 items-center gap-2.5 rounded-md px-3 text-[13px] no-underline transition-colors duration-150 motion-reduce:transition-none ${
					isActive
						? "bg-brand-subtle font-semibold text-brand-ink before:absolute before:inset-y-2 before:start-0 before:w-[3px] before:rounded-full before:bg-brand"
						: "font-medium text-fg-muted hover:bg-bg-subtle hover:text-fg"
				} ${className}`
			}
		>
			{icon !== undefined && <Icon as={icon} size={17} aria-hidden="true" className="shrink-0" />}
			{children}
		</NavLink>
	);
}

/**
 * What every account has whatever its grid (EP10-S12): its tickets — each
 * shown only when its source section is — and its own notification
 * preferences.
 */
function AccountLinks({ className = "" }: { className?: string }) {
	const { t } = useLocale();
	return (
		<>
			<MenuLink to="/tickets" icon={LifeBuoy} className={`mt-2 ${className}`}>
				{t("portal.shell.tickets")}
			</MenuLink>
			<MenuLink to="/notifications" icon={Bell} className={className}>
				{t("portal.shell.myNotifications")}
			</MenuLink>
		</>
	);
}

/** The store switcher: the store's name, and a choice when there is more than one. */
function StoreSwitcher() {
	const { t } = useLocale();
	const { stores, store, switchStore } = useStoreScope();
	if (store === null) {
		return null;
	}
	if (stores.length < 2) {
		return (
			<div className="flex min-h-10 items-center gap-2 px-1">
				<span
					aria-hidden="true"
					className="grid size-8 place-items-center rounded-sm bg-brand-subtle font-bold text-brand-ink"
				>
					{(store.storeName ?? store.storeId).slice(0, 1).toUpperCase()}
				</span>
				<span className="truncate font-bold text-fg">{store.storeName ?? store.storeId}</span>
			</div>
		);
	}
	return (
		<Select
			aria-label={t("portal.shell.switchStore")}
			value={store.storeId}
			onValueChange={switchStore}
			options={stores.map((s) => ({ value: s.storeId, label: s.storeName ?? s.storeId }))}
		/>
	);
}

/** The browser's zone, for the bell's relative times. */
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * The way out (ADJ-M03-4): our session ends before AuthKit's, through the
 * sequence every app shares. Inside a Login as Store tab there is no AuthKit
 * session to end — the token is forgotten and the reload lands on sign-in.
 * Pending while it runs, so a second tap does nothing.
 */
/** Sends a driver on to the Driver App, with a button for a browser that does not follow. */
function ToDriverApp({ href }: { href: string }) {
	const { t } = useLocale();
	useEffect(() => {
		window.location.replace(href);
	}, [href]);
	return (
		<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
			<EmptyState
				title={t("portal.shell.driverTitle")}
				body={t("portal.shell.driverBody")}
				action={
					<a
						href={href}
						className="flex min-h-12 items-center justify-center rounded-sm bg-brand px-4 text-sm font-bold text-fg-on-brand no-underline hover:bg-brand-hover"
					>
						{t("portal.shell.openDriverApp")}
					</a>
				}
			/>
		</main>
	);
}

function LeaveButton({
	label,
	variant,
	className = "min-h-12",
}: {
	label: string;
	variant: "primary" | "secondary";
	className?: string;
}) {
	const { signOut } = useAuth();
	const api = useApi();
	const [leaving, setLeaving] = useState(false);
	const leave = () => {
		setLeaving(true);
		if (impersonationToken() !== null) {
			forgetImpersonation();
			window.location.reload();
			return;
		}
		void endSession(api, signOut).finally(() => setLeaving(false));
	};
	return (
		<Button variant={variant} className={className} loading={leaving} onClick={leave}>
			{label}
		</Button>
	);
}

function Topbar() {
	const { t, locale, switchLanguage } = useLocale();
	const { context } = useStoreScope();
	const navigate = useNavigate();
	const inbox = useInboxSource(useApi());
	return (
		<header className="flex min-h-14 items-center justify-between gap-2 border-b border-border bg-bg-surface px-4">
			{/* `min-w-0 flex-1` so a long store name truncates rather than pushing
			    the bar's buttons off a phone's screen (ADJ-M01-1). */}
			<div className="min-w-0 flex-1 md:hidden">
				<StoreSwitcher />
			</div>
			<span className="hidden text-[13px] text-fg-muted md:block">
				{context?.me.displayName ?? ""}
			</span>
			<div className="flex shrink-0 items-center gap-2">
				<NotificationBell
					source={inbox}
					t={t}
					locale={locale}
					timeZone={timeZone}
					onNavigate={(to) => void navigate(to)}
				/>
				{/* On a phone the language and Sign out live on More (ADJ-M03-4),
				    so the bar keeps the store's name and the bell. */}
				<div className="hidden items-center gap-2 md:flex">
					<Button variant="ghost" onClick={switchLanguage}>
						{t("portal.shell.otherLanguage")}
					</Button>
					{/* Inside a Login as Store session the banner's End is the way out;
					    signing out here would sign the operator out of AuthKit. */}
					{impersonationToken() === null && (
						<LeaveButton variant="secondary" className="" label={t("portal.shell.signOut")} />
					)}
				</div>
			</div>
		</header>
	);
}

function BottomBar() {
	const { t } = useLocale();
	const { store } = useStoreScope();
	const serviceOff = useServiceOff(store?.storeId ?? "");
	return (
		<nav
			aria-label={t("portal.shell.bottomBar")}
			// Above the page's own sticky and positioned layers, and clear of the
			// phone's home indicator (ADJ-M01-1).
			className="fixed inset-x-0 bottom-0 z-[var(--z-topbar)] flex border-t border-border bg-bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
		>
			{bottomBar(store, serviceOff).map((key) => (
				<NavLink
					key={key}
					to={key === "more" ? "/more" : sectionPath(key)}
					className={({ isActive }) =>
						`flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] no-underline ${
							isActive ? "font-bold text-brand-ink" : "font-medium text-fg-muted"
						}`
					}
				>
					<Icon as={SECTION_ICONS[key]} size={20} aria-hidden="true" />
					<span className="max-w-full truncate px-1">
						{t(key === "more" ? "portal.shell.more" : `portal.shell.sections.${key}`)}
					</span>
				</NavLink>
			))}
		</nav>
	);
}

function Impersonation({
	id,
	target,
	endsAt,
	operator,
}: {
	id: string;
	target: string;
	endsAt: string;
	operator: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const end = useMutation({
		mutationFn: () => api.endImpersonation(id),
		// The token is dead: forget it, and the reload lands on sign-in.
		onSuccess: () => {
			forgetImpersonation();
			window.location.reload();
		},
	});
	return (
		<ImpersonationBanner
			operator={operator}
			target={target}
			endsAt={endsAt}
			onEnd={() => end.mutate()}
			ending={end.isPending}
			t={t}
		/>
	);
}

/** Said while the browser is offline; nothing is queued, so it says so. */
function OfflineBanner() {
	const { t } = useLocale();
	const [online, setOnline] = useState(() => navigator.onLine);
	useEffect(() => {
		const up = () => setOnline(true);
		const down = () => setOnline(false);
		window.addEventListener("online", up);
		window.addEventListener("offline", down);
		return () => {
			window.removeEventListener("online", up);
			window.removeEventListener("offline", down);
		};
	}, []);
	if (online) {
		return null;
	}
	return (
		<p role="status" className="bg-warning-bg px-4 py-2 text-center text-[12.5px] text-warning-fg">
			{t("portal.shell.offline")}
		</p>
	);
}

/**
 * Renders its children only when the account's level in the section reaches
 * what is asked — the wrapper every mutating control sits in.
 */
export function Can({
	section,
	level,
	children,
}: {
	section: SectionKey;
	level: Exclude<Level, "none">;
	children: ReactNode;
}) {
	const { store } = useStoreScope();
	return reaches(levelOf(store, section), level) ? children : null;
}

/**
 * A section whose service the store switched off (call note 2): what a direct
 * link lands on, with the way back on.
 */
export function ServiceGate({ section, children }: { section: SectionKey; children: ReactNode }) {
	const { t } = useLocale();
	const { store } = useStoreScope();
	const serviceOff = useServiceOff(store?.storeId ?? "");
	if (!sectionOff(section, serviceOff)) {
		return children;
	}
	return (
		<EmptyState title={t("portal.shell.serviceOffTitle")} body={t("portal.shell.serviceOffBody")} />
	);
}

/** The portal's landing: the first section the grid shows. */
export function Landing() {
	const { store } = useStoreScope();
	const serviceOff = useServiceOff(store?.storeId ?? "");
	const first = visibleSections(store, serviceOff)[0];
	return first === undefined ? <NothingShared /> : <Navigate to={sectionPath(first)} replace />;
}

function NothingShared() {
	const { t } = useLocale();
	return <EmptyState title={t("portal.shell.nothingTitle")} body={t("portal.shell.nothingBody")} />;
}

/** More, on a phone: every section the grid shows. */
export function MorePage() {
	const { t, switchLanguage } = useLocale();
	const { store } = useStoreScope();
	const serviceOff = useServiceOff(store?.storeId ?? "");
	return (
		<div className="flex flex-col gap-4">
			<nav aria-label={t("portal.shell.more")} className="flex flex-col gap-1">
				{visibleSections(store, serviceOff).map((key) => (
					<SectionLink key={key} section={key} className="min-h-12 border-b border-border" />
				))}
				<AccountLinks className="min-h-12 border-b border-border" />
			</nav>
			{/* The phone's top bar has no room for these (ADJ-M03-4). */}
			<div className="flex flex-wrap gap-2">
				<Button variant="secondary" className="min-h-12" onClick={switchLanguage}>
					{t("portal.shell.otherLanguage")}
				</Button>
				{impersonationToken() === null && (
					<LeaveButton variant="secondary" label={t("portal.shell.signOut")} />
				)}
			</div>
		</div>
	);
}

export function NotFound() {
	const { t } = useLocale();
	return (
		<EmptyState title={t("portal.shell.notFoundTitle")} body={t("portal.shell.notFoundBody")} />
	);
}

/** `/inbox` (EP12-S03): the caller's notifications, the console's page. */
export function InboxScreen() {
	const { t, locale } = useLocale();
	const navigate = useNavigate();
	return (
		<InboxPage
			api={useApi()}
			t={t}
			locale={locale}
			timeZone={timeZone}
			onNavigate={(to) => void navigate(to)}
		/>
	);
}
